import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse } from '@/types';

/**
 * API Client Configuration
 * Handles HTTP requests with authentication and error handling
 */

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'https://api.fittrack.site/api',
    timeout: 60000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        // Get token from localStorage (fallback if cookies don't work)
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Try to refresh token — use absolute URL so it doesn't hit a relative path
                const refreshResponse = await axios.post(
                    `${import.meta.env.VITE_API_URL || 'https://api.fittrack.site/api'}/auth/refresh`,
                    {},
                    { withCredentials: true }
                );

                if (refreshResponse.data.success) {
                    const newToken = refreshResponse.data.data.accessToken;
                    localStorage.setItem('accessToken', newToken);

                    // Retry original request with new token
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed, redirect to login
                localStorage.removeItem('accessToken');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// API helper functions
export const apiClient = {
    // Generic GET request
    get: async <T>(url: string, params?: any): Promise<ApiResponse<T>> => {
        const response = await api.get(url, { params });
        return response.data;
    },

    // Generic POST request
    post: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
        const response = await api.post(url, data);
        return response.data;
    },

    // Generic PUT request
    put: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
        const response = await api.put(url, data);
        return response.data;
    },

    // Generic PATCH request
    patch: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
        const response = await api.patch(url, data);
        return response.data;
    },

    // Generic DELETE request
    delete: async <T>(url: string): Promise<ApiResponse<T>> => {
        const response = await api.delete(url);
        return response.data;
    },
};

// Authentication API
export const authAPI = {
    register: (data: any) => apiClient.post('/auth/register', data),
    login: (data: any) => apiClient.post('/auth/login', data),
    logout: () => apiClient.post('/auth/logout'),
    logoutAll: () => apiClient.post('/auth/logout-all'),
    getMe: () => apiClient.get('/auth/me'),
    changePassword: (data: any) => apiClient.put('/auth/change-password', data),
    refreshToken: () => apiClient.post('/auth/refresh'),
    forgotPassword: (data: any) => apiClient.post('/auth/forgot-password', data),
    resetPassword: (data: any) => apiClient.post('/auth/reset-password', data),
};

// User API
export const notificationAPI = {
    list: () => apiClient.get('/notifications'),
    markAllRead: () => apiClient.post('/notifications/read', {}),
    clearAll: () => apiClient.delete('/notifications'),
};

export const userAPI = {
    getProfile: () => apiClient.get('/users/profile'),
    updateProfile: (data: any) => apiClient.put('/users/profile', data),
    getDashboard: (period?: string) => apiClient.get('/users/dashboard', period ? { period } : undefined),
    getUser: (identifier: string) => apiClient.get(`/users/${identifier}`),
    searchUsers: (query: string, params?: any) => apiClient.get('/users/search', { q: query, ...params }),
    followUser: (userId: string) => apiClient.post(`/users/${userId}/follow`),
    unfollowUser: (userId: string) => apiClient.delete(`/users/${userId}/follow`),
    getFollowers: (userId: string, params?: any) => apiClient.get(`/users/${userId}/followers`, params),
    getFollowing: (userId: string, params?: any) => apiClient.get(`/users/${userId}/following`, params),
};

// Exercise API
export const exerciseAPI = {
    getExercises: (params?: any) => apiClient.get('/exercises', params),
    getExercise: (id: string) => apiClient.get(`/exercises/${id}`),
    createExercise: (data: any) => apiClient.post('/exercises', data),
    updateExercise: (id: string, data: any) => apiClient.put(`/exercises/${id}`, data),
    deleteExercise: (id: string) => apiClient.delete(`/exercises/${id}`),
    getCategories: () => apiClient.get('/exercises/categories'),
    getMuscleGroups: () => apiClient.get('/exercises/muscle-groups'),
    getEquipment: () => apiClient.get('/exercises/equipment'),
    // Combined library: your exercises + free-exercise-db + ExerciseDB + wger
    searchLibrary: (params?: any) => apiClient.get('/exercises/catalog', params),
    getLibraryFilters: () => apiClient.get('/exercises/catalog/filters'),
    importFromLibrary: (catalogId: string) => apiClient.post('/exercises/catalog/import', { catalogId }),
    // Names -> loggable exercise ids in one request (ids align with names; null = no match)
    resolveNames: (names: string[]) => apiClient.post<{ ids: (string | null)[] }>('/exercises/catalog/resolve', { names }),
};

/** Returns a DB exercise id for a library item, importing it first if needed. */
export const ensureExerciseId = async (item: { dbId?: string | null; catalogId?: string | null; _id?: string; id?: string }): Promise<string> => {
    if (item.dbId) return item.dbId;
    if (!item.catalogId) return (item._id || item.id) as string;
    const res: any = await exerciseAPI.importFromLibrary(item.catalogId);
    return res.data.exercise.id;
};

/**
 * Resolves any exercise-like object (library item, DB exercise, or a static guide
 * entry that only has a name) to a DB exercise id that can be logged.
 */
export const resolveExerciseId = async (exercise: any): Promise<string> => {
    if (exercise.dbId || exercise.catalogId) return ensureExerciseId(exercise);
    const id = exercise._id || exercise.id;
    if (id && !String(id).startsWith('fallback-')) return id;

    const res: any = await exerciseAPI.searchLibrary({ search: exercise.name, limit: 1 });
    const match = res.data?.exercises?.[0];
    if (!match) throw new Error(`Couldn't find "${exercise.name}" in the exercise library`);
    return ensureExerciseId(match);
};

// Workout API
export const workoutAPI = {
    getWorkouts: (params?: any) => apiClient.get('/workouts', params),
    getWorkout: (id: string) => apiClient.get(`/workouts/${id}`),
    createWorkout: (data: any) => apiClient.post('/workouts', data),
    updateWorkout: (id: string, data: any) => apiClient.put(`/workouts/${id}`, data),
    deleteWorkout: (id: string) => apiClient.delete(`/workouts/${id}`),
    duplicateWorkout: (id: string) => apiClient.post(`/workouts/${id}/duplicate`),
    getPublicWorkouts: (params?: any) => apiClient.get('/workouts/public', params),
    getWorkoutStats: (params?: any) => apiClient.get('/workouts/stats', params),
};

// Goal API
export const goalAPI = {
    getGoals: (params?: any) => apiClient.get('/goals', params),
    getGoal: (id: string) => apiClient.get(`/goals/${id}`),
    createGoal: (data: any) => apiClient.post('/goals', data),
    updateGoal: (id: string, data: any) => apiClient.put(`/goals/${id}`, data),
    updateGoalProgress: (id: string, currentValue: number) =>
        apiClient.patch(`/goals/${id}/progress`, { currentValue }),
    deleteGoal: (id: string) => apiClient.delete(`/goals/${id}`),
    getGoalStats: () => apiClient.get('/goals/stats'),
};

// Social API
export const socialAPI = {
    getFeed: (params?: any) => apiClient.get('/social/feed', params),
    getDiscoverPosts: (params?: any) => apiClient.get('/social/discover', params),
    getUserPosts: (userId: string, params?: any) => apiClient.get(`/social/users/${userId}/posts`, params),
    createPost: (data: any) => apiClient.post('/social/posts', data),
    getPost: (id: string) => apiClient.get(`/social/posts/${id}`),
    updatePost: (id: string, data: any) => apiClient.put(`/social/posts/${id}`, data),
    deletePost: (id: string) => apiClient.delete(`/social/posts/${id}`),
    toggleLike: (postId: string) => apiClient.post(`/social/posts/${postId}/like`),
    addComment: (postId: string, content: string) =>
        apiClient.post(`/social/posts/${postId}/comments`, { content }),
    deleteComment: (postId: string, commentId: string) =>
        apiClient.delete(`/social/posts/${postId}/comments/${commentId}`),
    addReply: (postId: string, commentId: string, content: string) =>
        apiClient.post(`/social/posts/${postId}/comments/${commentId}/replies`, { content }),
};

// AI Coach API
export const coachAPI = {
    getPlateauAnalysis: () => apiClient.get('/coach/plateau'),
    getNutrition: () => apiClient.get('/coach/nutrition'),
    getFullAnalysis: () => apiClient.get('/coach/analyze'),
    /** Without a conversationId the server starts a new saved chat and returns its id. */
    chat: (
        message: string,
        conversationId: string | null,
        history: { role: 'user' | 'assistant'; content: string }[] = [],
    ) =>
        apiClient.post('/coach/chat', {
            message, conversationId, history, tzOffset: new Date().getTimezoneOffset(),
        }),
    listConversations: () => apiClient.get('/coach/conversations'),
    getConversation: (id: string) => apiClient.get(`/coach/conversations/${id}`),
    deleteConversation: (id: string) => apiClient.delete(`/coach/conversations/${id}`),
};

// FitCoin API
export const fitCoinAPI = {
    getWallet: () => apiClient.get('/fitcoin/wallet'),
    getStore: () => apiClient.get('/fitcoin/store'),
    getInventory: () => apiClient.get('/fitcoin/inventory'),
    purchase: (itemId: string) => apiClient.post(`/fitcoin/purchase/${itemId}`),
    equip: (itemId: string) => apiClient.post(`/fitcoin/equip/${itemId}`),
};

// Squad API
export const squadAPI = {
    getSquads: () => apiClient.get('/squads'),
    getMySquads: () => apiClient.get('/squads/mine'),
    createSquad: (data: any) => apiClient.post('/squads', data),
    joinSquad: (id: string) => apiClient.post(`/squads/${id}/join`),
    startSquad: (id: string) => apiClient.post(`/squads/${id}/start`),
    checkin: (id: string) => apiClient.post(`/squads/${id}/checkin`),
};

// Heartbeat API
export const heartbeatAPI = {
    getFriendsHeartbeats: () => apiClient.get('/heartbeat/friends'),
};

// Wearables API
export const wearableAPI = {
    getDevices: () => apiClient.get('/wearables/devices'),
    pairDevice: (data: any) => apiClient.post('/wearables/devices', data),
    toggleDevice: (deviceId: string) => apiClient.patch(`/wearables/devices/${deviceId}/toggle`, {}),
    unpairDevice: (deviceId: string) => apiClient.delete(`/wearables/devices/${deviceId}`),
    syncReadings: (data: any) => apiClient.post('/wearables/sync', data),
    // tzOffset lets the server total "today" in the user's own day, not UTC's.
    getDashboard: (period?: string) =>
        apiClient.get('/wearables/dashboard', { ...(period ? { period } : {}), tzOffset: new Date().getTimezoneOffset() }),
    getSyncStatus: () => apiClient.get('/wearables/sync-status'),
    generatePairingToken: () => apiClient.post('/wearables/pairing-token', {}),
    getPairingStatus: (token: string) => apiClient.get(`/wearables/pairing-status/${token}`),

    // Google Health / Fitbit OAuth
    getGoogleHealthAuthUrl: () => apiClient.get('/wearables/oauth/google-health/connect'),
    syncGoogleHealth: (days?: number) => apiClient.get(`/wearables/oauth/google-health/sync${days ? `?days=${days}` : ''}`),
    disconnectGoogleHealth: () => apiClient.delete('/wearables/oauth/google-health/disconnect'),
};

// Schedules API - Auto-tracking for recurring workouts
export const scheduleAPI = {
    getSchedules: (status?: 'active' | 'completed') => apiClient.get('/schedules', status ? { status } : undefined),
    createSchedule: (data: any) => apiClient.post('/schedules', data),
    updateSchedule: (id: string, data: any) => apiClient.patch(`/schedules/${id}`, data),
    deleteSchedule: (id: string) => apiClient.delete(`/schedules/${id}`),
    logCompletion: (scheduleId: string, data: any) => apiClient.post(`/schedules/${scheduleId}/log`, data),
    getStats: () => apiClient.get('/schedules/stats'),
};

export default api;